/**
 * Snapshot Management UI
 * 
 * Allows platform admins to view, compare, delete, and rollback from ML model snapshots.
 * Snapshots are created automatically before retraining and can be used to restore previous model states.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Camera, AlertCircle, CheckCircle, Clock, Trash2, RotateCcw, Eye, Filter } from 'lucide-react';
import SnapshotDetailModal from './components/SnapshotDetailModal';
import RollbackConfirmationModal from './components/RollbackConfirmationModal';
import { getApiBase, getAuthHeaders } from '../../utils/apiHelpers';

interface ModelMetadata {
  model_id: string;
  algorithm: string;
  model_version: string;
  accuracy?: number;
  precision?: number;
  recall?: number;
  f1_score?: number;
}

interface Snapshot {
  snapshot_id: string;
  tenant_id: string;
  snapshot_type: 'pre_retraining' | 'manual' | 'scheduled';
  trigger_reason: string | null;
  model_count: number;
  created_at: string;
  models?: ModelMetadata[];
}

interface SnapshotListResponse {
  snapshots: Snapshot[];
  total: number;
  tenant_id: string;
}

const SnapshotManagement: React.FC = () => {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [snapshotType, setSnapshotType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const fetchSnapshots = useCallback(async () => {
    if (!selectedTenant) {
      setError('Please select a tenant');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const typeParam = snapshotType ? `?snapshot_type=${snapshotType}` : '';
      const url = `${getApiBase()}/api/platform-admin/ml/snapshots/${selectedTenant}${typeParam}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch snapshots: ${response.statusText}`);
      }

      const data: SnapshotListResponse = await response.json();
      setSnapshots(data.snapshots);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch snapshots');
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, snapshotType]);

  const handleDeleteSnapshot = async (snapshotId: string) => {
    if (!selectedTenant) return;

    try {
      const url = `${getApiBase()}/api/platform-admin/ml/snapshots/${selectedTenant}/${snapshotId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to delete snapshot: ${response.statusText}`);
      }

      setSnapshots(snapshots.filter(s => s.snapshot_id !== snapshotId));
      setDeleteConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete snapshot');
    }
  };

  const handleViewDetails = (snapshot: Snapshot) => {
    setSelectedSnapshot(snapshot);
    setShowDetailModal(true);
  };

  const handleRollback = (snapshot: Snapshot) => {
    setSelectedSnapshot(snapshot);
    setShowRollbackModal(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'pre_retraining':
        return 'bg-blue-100 text-blue-800';
      case 'manual':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tenantId = params.get('tenant_id');
    if (tenantId) {
      setSelectedTenant(tenantId);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchSnapshots();
    }
  }, [selectedTenant, snapshotType, fetchSnapshots]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Camera className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Model Snapshots</h1>
        </div>
        <p className="text-gray-600">
          View and manage ML model snapshots for rollback and audit purposes
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tenant ID
            </label>
            <input
              type="text"
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              placeholder="Enter tenant ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Snapshot Type
            </label>
            <select
              value={snapshotType}
              onChange={(e) => setSnapshotType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="pre_retraining">Pre-Retraining</option>
              <option value="manual">Manual</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchSnapshots}
              disabled={!selectedTenant || loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Filter className="w-4 h-4" />
              {loading ? 'Loading...' : 'Load Snapshots'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {snapshots.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Snapshots</p>
                <p className="text-2xl font-bold text-gray-900">{snapshots.length}</p>
              </div>
              <Camera className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pre-Retraining</p>
                <p className="text-2xl font-bold text-blue-900">
                  {snapshots.filter(s => s.snapshot_type === 'pre_retraining').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Models</p>
                <p className="text-2xl font-bold text-green-900">
                  {snapshots.reduce((sum, s) => sum + s.model_count, 0)}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
      )}

      {/* Snapshots Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading snapshots...</p>
        </div>
      ) : snapshots.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Snapshots Found</h3>
          <p className="text-gray-600">
            {selectedTenant 
              ? 'No snapshots exist for this tenant yet. Snapshots are created automatically before retraining.'
              : 'Please select a tenant to view snapshots.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Snapshot ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trigger Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Models
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {snapshots.map((snapshot) => (
                <tr key={snapshot.snapshot_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">
                      {snapshot.snapshot_id.substring(0, 8)}...
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(snapshot.snapshot_type)}`}>
                      {snapshot.snapshot_type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {snapshot.trigger_reason || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {snapshot.model_count} models
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(snapshot.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetails(snapshot)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRollback(snapshot)}
                        className="text-green-600 hover:text-green-900 flex items-center gap-1"
                        title="Rollback"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      {deleteConfirmId === snapshot.snapshot_id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDeleteSnapshot(snapshot.snapshot_id)}
                            className="text-red-600 hover:text-red-900 text-xs font-semibold"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-gray-600 hover:text-gray-900 text-xs font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(snapshot.snapshot_id)}
                          className="text-red-600 hover:text-red-900 flex items-center gap-1"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showDetailModal && selectedSnapshot && (
        <SnapshotDetailModal
          snapshotId={selectedSnapshot.snapshot_id}
          tenantId={selectedSnapshot.tenant_id}
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
        />
      )}

      {showRollbackModal && selectedSnapshot && (
        <RollbackConfirmationModal
          snapshotId={selectedSnapshot.snapshot_id}
          tenantId={selectedSnapshot.tenant_id}
          modelCount={selectedSnapshot.model_count}
          isOpen={showRollbackModal}
          onClose={() => setShowRollbackModal(false)}
          onSuccess={() => {
            fetchSnapshots();
          }}
        />
      )}
    </div>
  );
};

export default SnapshotManagement;
